import useDiscard from "../../useDiscard";
import useMap from "../../utils/useMap";
import useWeakMap from "../../utils/useWeakMap";
import useQueryCopySource from "../chain/store/useStoreCopySource";
import { lookupParentLayer } from "../chain/store/useStoreLayerParent";
import useQueryPathByLayer from "../chain/store/useStorePathByLayer";
import useQueryOriginalSourceByLayer from "../chain/store/useStoreOriginalSource";
import useQueryLayerUsingOriginalSource from "../chain/store/useStoreLayerUsingOriginalSource";
import { getAllRoots, getRootProps, isRoot } from "../chain/store/useStoreRoot";
import { getLayerUID, isLayer } from "../chain/validator/useLayerIdentity";
import isAvoidEmitCase from "./avoidEmitCase";
import { emitOriginalSourceChanged } from "./useOnOriginalSourceChanged";
import { getLayerVersioning } from "../utils/useLayerVersioning";
import useLogStack from "../utils/useLogStack";
import { onChangeBoxDone } from "../utils/useChangeBox";
import useDebouncing from "../../utils/useDebouncing";

const ALL_PROPS = "*";

/**
 * @type {UseOnLayerAfterSet.store}
 */
const store = useWeakMap()
const storeLayerVersioning = useWeakMap();

const hasLayer = layer => store.has(layer);

const storePrivateKey = useMap();

const isPrivateKeyValid = (privateKey) => typeof privateKey === 'symbol' && storePrivateKey.has(privateKey);

const SYM_PROP_PRIVATE_KEY = Symbol('privatekey');
const generatePrivateKey = () => Symbol('layerAfterSetter');

const getItem = layer => store.get(layer);

const createDepsIfNotExist = (layer, keyDep) => {
    const item = store.get(layer);
    if (!item.has(keyDep)) {
        item.set(keyDep, new Set());
    }
}

const addItem = (layer, cb, keyDeps) => {
    if (keyDeps === ALL_PROPS) {
        createDepsIfNotExist(layer, ALL_PROPS);
        store.get(layer).get(ALL_PROPS).add(cb);
    } else if (Array.isArray(keyDeps)) {
        for (let i in keyDeps) {
            const k = keyDeps[i];
            createDepsIfNotExist(layer, k);
            store.get(layer).get(k).add(cb);
        }
    }
}

const syncVersioning = (layer, nextVersion) => {
    // const latestVersion = getLayerVersioning(layer);
    storeLayerVersioning.set(layer, nextVersion);
}

const deleteItem = (layer, cb, keyDeps) => {
    if (!store.has(layer)) {
        return
    }
    if (keyDeps === ALL_PROPS) {
        store.get(layer).get(ALL_PROPS).delete(cb);
    } else if (Array.isArray(keyDeps)) {
        for (let i in keyDeps) {
            const k = keyDeps[i];
            store.get(layer).get(k).delete(cb);
        }
    }
}

/**
 * @type {UseOnLayerAfterSet.useOnLayerAfterSet}
 */
const useOnLayerAfterSet = (layer, cb, keyDeps) => {
    if (typeof cb !== 'function' || !hasLayer(layer)) {
        return () => { };
    }
    if (!store.has(layer)) {
        return () => { };
    }
    if (keyDeps !== ALL_PROPS && !Array.isArray(keyDeps)) {
        return () => { };

    } else if (keyDeps.length === 0) {
        return () => { };
    }
    addItem(layer, cb, keyDeps);

    return () => deleteItem(layer, cb, keyDeps)
}

const emitter = useMap();

const doEmit = (absKey, propertyPath, layer, key, value, oldValue) => {

    const versionOfCallBacks = storeLayerVersioning.get(layer);
    const latestLayerVersion = getLayerVersioning(layer);
    if (latestLayerVersion === versionOfCallBacks) return;
    const item = getItem(layer);
    const originalSource = useQueryOriginalSourceByLayer(layer);
    const oldOriginalSourceValue = originalSource ? originalSource[key] : null;

    const copySource = useQueryCopySource(layer);

    // const path = useQueryPathByLayer(layer);
    // path.push(key)

    // absKey = path.pathToString()


    if (!item) return;
    if (item.has(absKey)) {
        item.get(absKey).forEach((cb) => {
            cb(propertyPath, absKey, key, value, oldValue)
        })
    }
    if (item.has(ALL_PROPS)) {
        item.get(ALL_PROPS).forEach((cb) => {
            cb(propertyPath, absKey, key, value, oldValue)
        })
    }

    if (originalSource) {
        emitOriginalSourceChanged(originalSource, propertyPath, absKey, key, value)
    }

    syncVersioning(layer, versionOfCallBacks + 1);

    useLogStack("SYNCVERSION", null, { layerUID: getLayerUID(layer), key, value, latestLayerVersion, versionOfCallBacks })

    // if (isLayer(value)) {
    //     const originalByValue = useQueryOriginalSourceByLayer(value);
    //     console.log("emitOriginalSourceChanged", propertyPath, originalByValue)
    // }
    // if (isLayer(oldValue)) {
    //     const originalByValue = useQueryOriginalSourceByLayer(oldValue);
    //     console.log("emitOriginalSourceChanged", propertyPath, originalByValue)
    // }
}

/**
 * 
 * @type {UseOnLayerAfterSet.emitLayerAfterSet} 
 */
const emitLayerAfterSet = (privateKey, propertyPath, key, value, oldValue) => {
    if (isAvoidEmitCase(key)) return
    if (!isPrivateKeyValid(privateKey)) return value;
    const layer = storePrivateKey.get(privateKey);
    propertyPath.push(key);
    const absKey = propertyPath.pathToString();

    doEmit(absKey, propertyPath, layer, key, value, oldValue);
    emitParents(layer, propertyPath, absKey, key, value, oldValue);
    emitRoot(layer, propertyPath, absKey, key, value, oldValue);
}


const emitRoot = (layer, propertyPath, absKey, key, value, oldValue) => {
    if (isRoot(layer)) return;
    const originalSource = useQueryOriginalSourceByLayer(layer);
    const rootByOriginal = useQueryLayerUsingOriginalSource(originalSource);
    const rootLayers = getAllRoots(layer);
    if (!rootLayers) return;

    for (let i in rootLayers) {
        // console.log("emit Root", { absKey, propertyPath, layer, key, value, oldValue, root: rootLayers[key]  })
        const rootLayer = rootLayers[i];
        const copySource = useQueryCopySource(layer);
        if (propertyPath.length === 1 && copySource[key] === value) continue;
        doEmit(absKey, propertyPath, rootLayers[i], key, value, oldValue)
    }
}


const emitParents = (layer, propertyPath, absKey, key, value, oldValue) => {
    const parents = lookupParentLayer(layer);
    for (let parent of parents) {
        doEmit(absKey, propertyPath, parent, key, value, oldValue)
    }
}

/**@type {UseOnLayerAfterSet.useOnLayerAfterSetForwarder} */
export const useOnAfterSetForwarder = (fromLayer, toLayer, cb, deps) => {
    if (fromLayer === toLayer) { return () => { } }
    return useOnLayerAfterSet(fromLayer, (paths, absKey, key, value, oldValue) => {
        let rePaths = paths,
            reAbsKey = absKey,
            reKey = key,
            reValue = value,
            reOldValue = oldValue
        if (typeof cb === 'function') {
            const out = cb(paths, absKey, key, value, oldValue)
            rePaths = out[0] || rePaths;
            reAbsKey = out[1] || reAbsKey;
            reKey = out[2] || reKey;
            reValue = out[3] || reValue;
            reOldValue = out[4] || reOldValue;
        }
        emitLayer(toLayer, rePaths, reAbsKey, reKey, reValue, reOldValue);
    }, deps)
}

const emitLayer = (layer, propertyPath, absKey, key, value, oldValue) => {
    // const rootLayer = getRootProps(layer);
    // if (!rootLayer) return;

    doEmit(absKey, propertyPath, layer, key, value, oldValue)
}



const onsDiscardLayer = (layer) => {
    store.delete(layer);
    storePrivateKey.delete(store.get(SYM_PROP_PRIVATE_KEY));
    storeLayerVersioning.delete(layer);
}

/* Must init first, if not, the trigger will not work */
const initOnLayerAfterSet = (layer) => {
    if (hasLayer(layer)) {
        return
    }
    const privateKey = generatePrivateKey();
    const layerVersion = getLayerVersioning()
    storePrivateKey.set(privateKey, layer);
    storeLayerVersioning.set(layer, layerVersion);
    store.set(layer, useMap());
    store.get(layer).set(SYM_PROP_PRIVATE_KEY, privateKey)
    /* Destroy when layer discard */
    useDiscard(layer, onsDiscardLayer)

    return privateKey
}



export default useOnLayerAfterSet
export {
    emitLayerAfterSet,
    initOnLayerAfterSet
}